import { createSlice } from "@reduxjs/toolkit";

const initialState = {
  onboardingDialog: {
    open: false,
    placeId: null,
  },

  setupSnackbar: {
    open: false,
  },

  guide: {
    active: false,
    currentStep: null,
    completedSteps: [],
    skippedSteps: [],
    navigating: false,
    transit: false,   // true while user is performing action — overlay hidden but guide stays active
    userRole: null,   // "Super Admin" | "Brand Admin" — determines step flow
  },

  // Tracks the ID of a just-created place so onboarding targets it specifically
  newlyCreatedPlaceId: null,
};

const uiSlice = createSlice({
  name: "ui",
  initialState,

  reducers: {
    /* ---------------- ONBOARDING ---------------- */

    openOnboarding: (state, action) => {
      state.onboardingDialog.open = true;
      state.onboardingDialog.placeId = action.payload;

      // prevent snackbar during onboarding
      state.setupSnackbar.open = false;
    },

    closeOnboarding: (state) => {
      state.onboardingDialog.open = false;
      state.onboardingDialog.placeId = null;
    },

    /* ---------------- SNACKBAR ---------------- */

    triggerSetupSnackbar: (state, action) => {
      // NEVER show snackbar while guide active
      if (state.guide.active) {
        state.setupSnackbar.open = false;
        return;
      }

      const setup = action.payload;

      if (!setup) {
        state.setupSnackbar.open = true;
        return;
      }

      const steps = setup?.steps || {};
      const skipped = setup?.skipped || {};

      if (skipped?.isSkipped || skipped?.isPermanent) {
        state.setupSnackbar.open = false;
        return;
      }

      const hasIncompleteSteps =
        Object.keys(steps).length > 0 &&
        Object.values(steps).some((step) => step === false);

      state.setupSnackbar.open = hasIncompleteSteps;
    },

    closeSetupSnackbar: (state) => {
      state.setupSnackbar.open = false;
    },

    /* ---------------- GUIDE ---------------- */

    startGuide: (state, action) => {
      // payload may be a plain step string OR { step, role }
      if (typeof action.payload === "object" && action.payload !== null) {
        state.guide.active = true;
        state.guide.currentStep = action.payload.step;
        state.guide.userRole = action.payload.role ?? state.guide.userRole;
      } else {
        state.guide.active = true;
        state.guide.currentStep = action.payload;
      }
      state.guide.navigating = false;
    },

    stopGuide: (state) => {
      state.guide.active = false;
      state.guide.currentStep = null;
      state.guide.navigating = false;
      state.guide.transit = false;
    },

    /* Put guide in transit: still active (events fire) but UI hidden */
    setGuideTransit: (state, action) => {
      state.guide.transit = action.payload;
    },

    nextStep: (state, action) => {
      state.guide.active = true;
      state.guide.currentStep = action.payload;
      state.guide.navigating = false;
      state.guide.transit = false; // always show card when step advances
    },

    setGuideNavigating: (state, action) => {
      state.guide.navigating = action.payload;
    },

    completeGuideStep: (state, action) => {
      const step = action.payload;

      if (!state.guide.completedSteps.includes(step)) {
        state.guide.completedSteps.push(step);
      }
    },

    skipGuideStep: (state, action) => {
      const step = action.payload;

      if (!state.guide.skippedSteps.includes(step)) {
        state.guide.skippedSteps.push(step);
      }
    },

    /* Track newly created place for onboarding targeting */
    setNewlyCreatedPlaceId: (state, action) => {
      state.newlyCreatedPlaceId = action.payload;
    },

    clearNewlyCreatedPlaceId: (state) => {
      state.newlyCreatedPlaceId = null;
    },
  },
});

export const {
  openOnboarding,
  closeOnboarding,
  triggerSetupSnackbar,
  closeSetupSnackbar,
  startGuide,
  stopGuide,
  nextStep,
  setGuideNavigating,
  setGuideTransit,
  completeGuideStep,
  skipGuideStep,
  setNewlyCreatedPlaceId,
  clearNewlyCreatedPlaceId,
} = uiSlice.actions;

export default uiSlice.reducer;
